import React, { useState, useEffect } from "react";
import { Download, Volume2, Trash2, ShieldCheck } from "lucide-react";
import { useApp } from "../context/AppContext";
import { exportAll } from "../lib/db";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";

export default function Settings() {
  const { voiceURI, chooseVoice, unlocked, sessions } = useApp();
  const [voices, setVoices] = useState([]);

  useEffect(() => {
    const load = () => setVoices(window.speechSynthesis?.getVoices() || []);
    load();
    if (window.speechSynthesis) window.speechSynthesis.onvoiceschanged = load;
  }, []);

  const label = (v) => {
    const name = v.name;
    const lower = name.toLowerCase();
    let gender = /female|zira|samantha|victoria|karen|tessa|fiona|moira/.test(lower) ? "Female"
      : /male|david|daniel|alex|fred|thomas|rishi/.test(lower) ? "Male" : "Voice";
    return `${gender} — ${name} (${v.lang})`;
  };

  const preview = () => {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance("This is your selected Acuman voice.");
    const v = voices.find((x) => x.voiceURI === voiceURI);
    if (v) u.voice = v;
    window.speechSynthesis.speak(u);
  };

  const doExport = async () => {
    const data = await exportAll();
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `acuman-backup-${new Date().toISOString().slice(0, 10)}.json`;
    a.click(); URL.revokeObjectURL(url);
    toast.success("Backup downloaded");
  };

  return (
    <div className="max-w-3xl mx-auto px-5 py-10">
      <h1 className="text-3xl font-black tracking-tighter" style={{ color: "#1E293B" }}>Settings</h1>

      <div className="mt-8 rounded-2xl border bg-white p-7 shadow-sm" style={{ borderColor: "#CBD5E1" }}>
        <div className="flex items-center gap-2 mb-1">
          <Volume2 size={18} color="#2563EB" />
          <h2 className="text-lg font-bold" style={{ color: "#1E293B" }}>Voice Preference</h2>
        </div>
        <p className="text-sm mb-4" style={{ color: "#475569" }}>Choose the voice used for the "Listen" button on the Conversation page.</p>
        <div className="flex gap-3 flex-wrap">
          <div className="flex-1 min-w-[220px]">
            <Select value={voiceURI || ""} onValueChange={chooseVoice}>
              <SelectTrigger data-testid="voice-select" className="rounded-xl" style={{ borderColor: "#CBD5E1" }}>
                <SelectValue placeholder="System default voice" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {voices.length === 0 && <SelectItem value="none" disabled>No voices detected</SelectItem>}
                {voices.map((v) => (<SelectItem key={v.voiceURI} value={v.voiceURI}>{label(v)}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <button onClick={preview} data-testid="preview-voice-btn"
            className="h-11 px-5 rounded-xl text-sm font-bold border transition-colors duration-200"
            style={{ borderColor: "#CBD5E1", color: "#1E293B", background: "#fff" }}>Preview</button>
        </div>
      </div>

      <div className="mt-6 rounded-2xl border bg-white p-7 shadow-sm" style={{ borderColor: "#CBD5E1" }}>
        <div className="flex items-center gap-2 mb-1">
          <Download size={18} color="#2563EB" />
          <h2 className="text-lg font-bold" style={{ color: "#1E293B" }}>Export All Data</h2>
        </div>
        <p className="text-sm mb-4" style={{ color: "#475569" }}>
          Download a complete backup of your Acuman library ({sessions.length} sessions) as a single .json file.
        </p>
        <button onClick={doExport} data-testid="export-data-btn"
          className="inline-flex items-center gap-2 h-11 px-5 rounded-xl text-sm font-bold text-white transition-colors duration-200" style={{ background: "#10B981" }}>
          <Download size={16} /> Export backup
        </button>
      </div>

      <div className="mt-6 rounded-2xl border p-7 shadow-sm" style={{ borderColor: unlocked ? "#10B98155" : "#CBD5E1", background: unlocked ? "#ECFDF5" : "#fff" }}>
        <div className="flex items-center gap-2">
          <ShieldCheck size={18} color={unlocked ? "#047857" : "#94A3B8"} />
          <h2 className="text-lg font-bold" style={{ color: "#1E293B" }}>Plan</h2>
        </div>
        <p className="text-sm mt-1" style={{ color: "#475569" }}>
          {unlocked ? "Unlimited — thank you for supporting Acuman! ☕" : "Free plan · 5 sessions included. Unlock unlimited for a one-time $5."}
        </p>
      </div>

      <div className="mt-8 flex items-center gap-3 justify-center">
        <a href="https://buymeacoffee.com/acuman" target="_blank" rel="noreferrer" data-testid="settings-tip-jar"
          className="text-sm font-semibold" style={{ color: "#047857" }}>☕ Support the project — Tip Jar</a>
      </div>
    </div>
  );
}
